import type { Transition } from "@uirouter/core";

import { sentryErrorTrackerWithScope } from "app/pages/sales-intelligence/utils/tracking/sentry";

import type { ILeadGeneratorRoutingHooks } from "./leadGeneratorRoutingHooks";

/**
 * Creates a proxy wrapper for routing hooks that adds error handling
 * to prevent navigation blocking on failures
 *
 * @returns Proxied hooks with error handling
 */
export function createRoutingHooksProxy<T extends ILeadGeneratorRoutingHooks>(hooks: T): T {
    return new Proxy(hooks, {
        get<K extends keyof ILeadGeneratorRoutingHooks>(
            target: ILeadGeneratorRoutingHooks,
            property: K,
        ): ILeadGeneratorRoutingHooks[K] {
            const original = target[property];

            if (property === "onEnterPrefetchThread" && typeof original === "function") {
                return async function wrappedOnEnterPrefetchThread(transition: Transition): Promise<void> {
                    try {
                        return original.call(target, transition) as Promise<void>;
                    } catch (error) {
                        // Don't block navigation on prefetch errors
                        sentryErrorTrackerWithScope({
                            errorMessage: `Failed to execute ${String(property)} hook`,
                            e: error,
                            params: {
                                extra: {
                                    transition: {
                                        to: transition.to().name,
                                        from: transition.from()?.name,
                                        params: transition.params(),
                                    },
                                },
                            },
                        });
                    }
                } as ILeadGeneratorRoutingHooks[K];
            }

            return original;
        },
    });
}
